import React, { useState, useEffect } from 'react';
import { autoSignIn, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextInput, Button, Container, Paper, Stack, Text } from '@mantine/core';

function VerifyEmail() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const username = location.state?.username;

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    try {
      await resendSignUpCode({ username });
      setCountdown(60);
      setError('');
      setSuccessMessage('A new verification code has been sent to your email');
      
      setTimeout(() => setSuccessMessage(''), 60000);
    } catch (error) {
      setError(error.message || 'Failed to send verification code');
      setSuccessMessage('');
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await confirmSignUp({
        username: username,
        confirmationCode: code
      });
      
      if (response.isSignUpComplete && response.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        const nextStep = await autoSignIn();
        if (nextStep.signInStep === "DONE") {
          navigate('/business-info');
        } else {
          navigate('/login', { state: { successMessage: 'Email verified!  Please sign in using your new credentials.' } });
        }
      }
    } catch (error) {
      setError(error.message || 'Failed to verify email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Paper radius="md" p="xl" withBorder>
        <Text size="xl" fw={500} ta="center" mb="xl">
          Verify Your Email
        </Text>

        <form onSubmit={handleVerification}>
          <Stack gap="xl">
            {error && (
              <Text c="red" size="sm" ta="center">
                {error}
              </Text>
            )}
            
            {successMessage && (
              <Text c="green" size="sm" ta="center">
                {successMessage}
              </Text>
            )}

            <TextInput
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXXXX"
              radius="md"
              required
            />

            <Stack gap="xs">
              <Button 
                type="submit" 
                radius="xl"
                loading={isLoading}
                fullWidth
              >
                Verify Email
              </Button>

              <Button
                variant="transparent"
                onClick={handleResendCode}
                sx={(theme) => ({
                  color: countdown > 0 ? theme.colors.gray[6] : theme.colors.violet[6],
                  fontSize: theme.fontSizes.sm,
                  cursor: countdown > 0 ? 'default' : 'pointer',
                  textAlign: 'center',
                  '&:hover': {
                    textDecoration: countdown > 0 ? 'none' : 'underline',
                  },
                })}
              >
                {countdown > 0 
                  ? `Send new confirmation code (${countdown}s)`
                  : 'Send new confirmation code'
                }
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

export default VerifyEmail; 